<template>
  <b-container
    class="mb-5"
    fluid
  >
    <div class="d-flex justify-content-end mb-3 pb-0">
      <b-breadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <b-card>
      <b-row class="mb-2">
        <b-col
          cols="12"
        >
          <div class="form-group">
            <label>
              <strong>Search</strong>
            </label>
            <b-input
              v-model="tableSuppliers.filter.search"
              type="text"
              placeholder="search here"
              debounce="1000"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
        >
          <b-button
            type="button"
            variant="success"
            autocomplete="off"
            @click="onCreate"
          >
            New Supplier
          </b-button>
        </b-col>

      </b-row>
      <b-table
        ref="tableSuppliers"
        hover
        responsive
        class="mt-2"
        :per-page="tableSuppliers.perPage"
        :current-page="tableSuppliers.currentPage"
        :items="tableProvider"
        :fields="tableSuppliers.fields"
        :sort-by.sync="tableSuppliers.sortBy"
        :sort-desc.sync="tableSuppliers.sortDesc"
        :sort-direction="tableSuppliers.sortDirection"
        :filter="tableSuppliers.filter"
        :filter-included-fields="tableSuppliers.filterOn"
        show-empty
      >
        <template #cell(index)="data">
          {{ tableSuppliers.currentPage * tableSuppliers.perPage - tableSuppliers.perPage + (data.index + 1) }}
        </template>

        <template #cell(active)="data">
          {{ data.item.active == 1 ? 'Yes' : 'No' }}
        </template>

        <template #cell()="data">
          <div class="text-nowrap">
            {{ data.value }}
          </div>
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="primary"
            @click="onEdit(row.item)"
          >
            Edit
          </b-button>
        </template>

      </b-table>
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <div class="w-100 w-sm-25 mb-2 sm-mb-2">
            <b-select
              v-model="tableSuppliers.perPage"
              :options="tableSuppliers.pageOptions"
              size="sm"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="tableSuppliers.currentPage"
            :total-rows="tableSuppliers.totalRows"
            :per-page="tableSuppliers.perPage"
            first-number
            last-number
            pills
            prev-text="Prev"
            next-text="Next"
            aria-controls="table"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      id="modal-supplier-form"
      scrollable
      no-close-on-backdrop
      no-enforce-focus
      aria-hidden="false"
      :title="modalTitle"
      @ok="onValidate"
    >
      <ValidationObserver
        ref="formSupplier"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <ValidationProvider
            #default="{ errors }"
            name="supplier name"
            vid="supplier_name"
            rules="required|max:150"
          >
            <div class="form-group">
              <label for="supplier_name">
                <strong>Supplier Name</strong>
              </label>
              <b-input
                id="supplier_name"
                v-model="supplier.supplier_name"
                type="text"
                placeholder="enter supplier name"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
                :disabled="state.busy"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </div>
          </ValidationProvider>

          <div
            v-if="state.editing"
            class="form-group"
          >
            <b-form-checkbox
              id="is_active"
              v-model="supplier.active"
              name="is_active"
              value="1"
              unchecked-value="0"
              switch
              :disabled="state.busy"
            >
              Is Active?
            </b-form-checkbox>
          </div>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ok, cancel}">
        <b-button
          variant="success"
          @click="ok()"
        >
          {{ state.editing ? 'Update Record' : 'Save Record' }}
        </b-button>
        <b-button
          variant="outline-dark"
          @click="cancel()"
        >
          Close Window
        </b-button>
      </template>

    </b-modal>
  </b-container>
</template>

<script>
import _ from 'lodash'
import { core } from '@/config/pluginInit'
import { AxiosError, SLOSupplier } from '@/services'
import formatter from '@/mixins/formatter'

export default {
  name: 'LogisticsOfficerSuppliers',

  middleware: ['auth', 'logistics-officer'],

  metaInfo () {
    return {
      title: 'Suppliers'
    }
  },

  mixins: [formatter],

  data () {
    return {
      state: {
        editing: false,
        busy: false
      },
      supplier: {
        id: 0,
        supplier_name: '',
        active: 0
      },
      tableSuppliers: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: '',
        sortDesc: false,
        sortDirection: 'asc',
        filter: {
          search: ''
        },
        filterOn: [],
        fields: [
          'index',
          'actions',
          { key: 'created_at', formatter: this.dateTimeFormatter, sortable: true },
          { key: 'supplier_name', sortable: true },
          { key: 'active' },
          { key: 'updated_at', formatter: this.dateTimeFormatter, sortable: true }
        ]
      }
    }
  },

  computed: {
    modalTitle () {
      return this.state.editing ? 'Edit Supplier' : 'New Supplier'
    }
  },

  mounted () {
    core.index()
  },

  methods: {
    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableSuppliers.busy = true
      const filters = this.objectToUrl({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      })

      return await SLOSupplier.get(filters).then(
        ({ data }) => {
          this.tableSuppliers.totalRows = data.total_rows
          return data.items
        }
      )
        .catch(() => {
          this.tableSuppliers.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableSuppliers.busy = false
        })
    },

    onCreate () {
      this.state.editing = false
      this.supplier.supplier_name = ''
      this.supplier.active = 1
      this.$bvModal.show('modal-supplier-form')
    },

    onEdit (item) {
      this.state.editing = true
      this.supplier.id = item.id
      this.supplier.supplier_name = item.supplier_name
      this.supplier.active = item.active
      this.$bvModal.show('modal-supplier-form')
    },

    async onValidate (bvModalEvt) {
      bvModalEvt.preventDefault()
      await this.$refs.formSupplier.validate().then(
        async areAllFieldsValid => {
          if (areAllFieldsValid) {
            this.$swal.fire({
              icon: 'question',
              title: 'Confirmation',
              text: this.state.editing ? 'Update Supplier?' : 'Create Supplier?',
              confirmButtonColor: '#06C270',
              confirmButtonText: 'Confirm',
              cancelButtonColor: '#737373',
              showCancelButton: true,
              showLoaderOnConfirm: true,
              preConfirm: () => {
                this.state.busy = true
                if (this.state.editing) {
                  return this.onPut()
                }

                return this.onPost()
              },
              allowOutsideClick: () => !this.$swal.isLoading()
            })
          } else {
            this.$bvModal.msgBoxOk('Oppss! There were problem with your inputs.', {
              title: 'Validation Error',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              centered: true
            })
          }
        }
      )
    },

    async onPost () {
      return new Promise(
        resolve => {
          SLOSupplier.post(this.supplier).then(
            ({ data }) => {
              this.$bvModal.hide('modal-supplier-form')
              this.$swal.fire({
                icon: 'success',
                title: 'Successful',
                text: data.message,
                confirmButtonColor: '#06C270',
                confirmButtonText: 'Dismiss'
              }).then(() => {
                this.$refs.tableSuppliers.refresh()
              })
            }
          ).catch(
            error => {
              if (error instanceof AxiosError) {
                if (error.code === 422) {
                  this.$refs.formSupplier.setErrors(error.message)
                }
              }
            }
          ).finally(resolve)
        }
      ).then(() => {
        this.state.busy = false
      })
    },

    async onPut () {
      return new Promise(
        resolve => {
          SLOSupplier.put(this.supplier).then(
            ({ data }) => {
              this.$bvModal.hide('modal-supplier-form')
              this.$swal.fire({
                icon: 'success',
                title: 'Successful',
                text: data.message,
                confirmButtonColor: '#06C270',
                confirmButtonText: 'Dismiss'
              }).then(() => {
                const row = _.find(this.$refs.tableSuppliers.localItems, {
                  id: data.supplier.id
                })
                row.supplier_name = data.supplier.supplier_name
                row.active = data.supplier.active
                row.updated_at = data.supplier.updated_at
              })
            }
          ).catch(error => {
            if (error instanceof AxiosError) {
              if (error.code === 422) {
                this.$refs.formSupplier.setErrors(error.message)
              }
            }
          }).finally(resolve)
        }
      ).then(() => {
        this.state.busy = false
      })
    }
  }
}
</script>
